import { theme } from '@Guidelines/Theme/theme';
import formatWholeEuros from '@UI/Pages/Administration/AiCosts/Helpers/formatWholeEuros';
import resolveSubjectLabel from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/resolveSubjectLabel';
import subjectLimitLevels from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/Helpers/subjectLimitLevels';
import type { SubjectDimension } from '@UI/Pages/Administration/AiCosts/CostTabs/SubjectCosts/Helpers/subjectDimensionLabels';
import {
	openAiCostLimitCreation,
	openAiCostLimitEdition,
} from '@UI/Pages/Administration/Shared/Signals/aiCostLimitSidebarSignal';
import type AiCostBreakdownRowView from '@UI/Store/AiCosts/Views/AiCostBreakdownRowView';
import type AiCostLimitSubjectsView from '@UI/Store/AiCostLimits/Views/AiCostLimitSubjectsView';
import type AiCostLimitView from '@UI/Store/AiCostLimits/Views/AiCostLimitView';
import type AiCostLimitsListView from '@UI/Store/AiCostLimits/Views/AiCostLimitsListView';
import { css } from '@emotion/react';
import * as React from 'react';
import { ProgressBar } from '@verclam/ui-kit';

const barColors: Readonly<Record<AiCostLimitView['statusSeverity'], string>> = {
	success: theme.colors.validated,
	warning: theme.states.warning.solid,
	danger: theme.states.danger.solid,
};

const barStyle = { height: '6px' };

const textStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const actionStyle = css`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.25rem;
	min-width: 8rem;
	padding: 0;
	border: 0;
	background: none;
	font: inherit;
	color: inherit;
	text-align: left;
	cursor: pointer;
`;

const defineStyle = css`
	${textStyle}
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0;
	border: 0;
	background: none;
	cursor: pointer;
	color: ${theme.colors.accentDark};
	font-weight: ${theme.fontWeights.semibold};
`;

type Props = {
	readonly row: AiCostBreakdownRowView;
	readonly dimension: SubjectDimension;
	readonly limits: AiCostLimitsListView;
	readonly subjects: AiCostLimitSubjectsView;
	/** Whether the period is the current month, the only one for which a consumed share makes sense. */
	readonly showsConsumption: boolean;
};

/** The limit owned by a row, or the way to define one; the click never reaches the row's own drill-down. */
const LimitCell: React.FC<Props> = ({ row, dimension, limits, subjects, showsConsumption }) => {
	const level = subjectLimitLevels[dimension];
	const subjectId = row.subjectId === null || row.isSubjectDeleted ? null : row.subjectId;
	const limit = subjectId === null ? null : limits.findBySubject(level, subjectId);
	const subjectName = resolveSubjectLabel(dimension, row).title;

	const handleEdit = React.useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();

			if (limit !== null) {
				openAiCostLimitEdition(limit.id);
			}
		},
		[limit],
	);

	const handleDefine = React.useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();

			if (subjectId !== null) {
				openAiCostLimitCreation(level, subjectId);
			}
		},
		[level, subjectId],
	);

	if (subjectId === null) {
		return <span css={textStyle}>—</span>;
	}

	if (limit !== null && showsConsumption) {
		return (
			<button
				type="button"
				css={actionStyle}
				aria-label={`Modifier le plafond de ${subjectName}`}
				onClick={handleEdit}
			>
				<span
					css={textStyle}
				>{`${limit.percent} % de ${formatWholeEuros(limit.monthlyAmount)}`}</span>
				<ProgressBar
					value={Math.min(100, limit.percent)}
					showValue={false}
					color={barColors[limit.statusSeverity]}
					style={barStyle}
				/>
			</button>
		);
	}

	if (limit !== null) {
		return (
			<button
				type="button"
				css={actionStyle}
				aria-label={`Modifier le plafond de ${subjectName}`}
				onClick={handleEdit}
			>
				<span css={textStyle}>{`${formatWholeEuros(limit.monthlyAmount)} / mois`}</span>
			</button>
		);
	}

	if (!subjects.covers(level, subjectId)) {
		return null;
	}

	return (
		<span css={textStyle}>
			—{' '}
			<button
				type="button"
				css={defineStyle}
				aria-label={`Définir un plafond pour ${subjectName}`}
				onClick={handleDefine}
			>
				+ Définir
			</button>
		</span>
	);
};

export default LimitCell;
